import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, isAdmin } from '../lib/supabase';

interface AuthUser {
  id: string;
  email: string;
  app_metadata?: {
    role?: string;
  };
  user_metadata: {
    full_name?: string;
  };
}

type User = {
  id: string;
  name: string;
  email: string;
  avatar: string;
  level: number;
  points: number;
  followers: number;
  following: number;
  performances: number;
  isSubscribed: boolean;
  role?: string;
};

type Profile = {
  id: string;
  username: string;
  full_name: string;
  avatar_url: string | null;
  role: 'user' | 'admin' | 'technical';
  coins: number;
};

type UserContextType = {
  user: User | null;
  profile: Profile | null;
  authInitialized: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTechnical: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      if (session?.user) {
        handleUser(session.user);
      } else {
        setAuthInitialized(true);
        setUser(null);
        setProfile(null);
        setCurrentUserId(null);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      console.log('Auth state change event:', event, 'has session:', !!session);

      if (session?.user) {
        // Only handle user if it's a different user than current
        if (session.user.id !== currentUserId) {
          handleUser(session.user);
        } else if (!authInitialized) {
          // If same user but not initialized yet, set as initialized
          setAuthInitialized(true);
        }
      } else {
        // No session - clear everything
        setAuthInitialized(true);
        setUser(null);
        setProfile(null);
        setCurrentUserId(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [currentUserId]);

  const handleUser = async (authUser: AuthUser) => {
    try {
      // Set current user ID first to prevent redundant calls
      setCurrentUserId(authUser.id);
      
      console.log('UserContext: Handling user with ID:', authUser.id);
      
      // Combine profile fetch and admin check into a single query
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (profileError) {
        console.error('UserContext: Error fetching profile:', profileError);
        // If profile doesn't exist, still set basic user info
        setAuthInitialized(true);
        setUser({
          id: authUser.id,
          name: authUser.user_metadata.full_name || 'User',
          email: authUser.email || '',
          avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
          level: 1,
          points: 0,
          followers: 0,
          following: 0,
          performances: 0,
          isSubscribed: false,
          role: 'user'
        });
        return;
      }

      console.log('UserContext: Got profile:', profile);

      // Set profile with all fields including role
      setProfile(profile);

      setAuthInitialized(true);
      setUser({
        id: authUser.id,
        name: profile?.full_name || authUser.user_metadata.full_name || 'User',
        email: authUser.email || '',
        avatar: profile?.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
        level: 1,
        points: 0,
        followers: 0,
        following: 0,
        performances: 0,
        isSubscribed: false,
        role: profile?.role || 'user'
      });
    } catch (error) {
      setAuthInitialized(true);
      console.error('Error loading user profile:', error);
      setUser(null);
      setProfile(null);
      setCurrentUserId(null);
    }
  };

  const login = async (email: string, password: string) => {
    const { data: { user: authUser }, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    // Don't call handleUser here as the auth state change listener will handle it
  };

  const logout = async () => {
    console.log('Logout initiated - clearing all state');

    // First, clear the session from storage synchronously
    try {
      // Manually clear localStorage to ensure session is removed
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('supabase.auth.token') || key.includes('sb-')) {
          console.log('Removing storage key:', key);
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }

    // Clear local state
    setUser(null);
    setProfile(null);
    setCurrentUserId(null);
    setAuthInitialized(true);

    // Finally call signOut (this may fail, but we don't care since state is cleared)
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore errors - we've already cleared everything
    }

    console.log('Logout completed');
  };

  return (
    <UserContext.Provider
      value={{
        user,
        profile,
        authInitialized,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isTechnical: user?.role === 'technical',
        login,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};